import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Box, alpha, InputAdornment,
  FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Typography,
} from '@mui/material';
import { Save as SaveIcon, AttachMoney as MoneyIcon, Title as TitleIcon, Notes as NotesIcon, Straighten as SizeIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import { getFirstFieldError, rejectConfirmValidation } from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import TextField from '@mui/material/TextField';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessPartnershipManagement } from '../../../utils/partnershipPermissions';
import { LOGO_SIZE_OPTIONS } from '../../../utils/partnerPublicDisplay';
import PartnerPublicPreview from './PartnerPublicPreview';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const TEXT_BODY = '#E2E8F0';

// ─── CSS keyframes ─────────────────────────────────────────────────────────────
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
`;

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── Shared input sx ───────────────────────────────────────────────────────────
const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.45), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: NEON },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': {
    color: `${alpha(TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`,
    '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
  '& .MuiOutlinedInput-root .MuiSelect-select': {
    color: `${alpha(TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`,
  },
};

// ─── StyledField ──────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

// ─── StyledSelect ─────────────────────────────────────────────────────────────
const StyledSelect = ({ label, value, onChange, children, required, startIcon, helperText, error, sx, name }) => (
  <FormControl fullWidth required={required} error={error} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select
      value={value} onChange={onChange} label={label} name={name}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{
        PaperProps: {
          sx: {
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
            backdropFilter: 'blur(12px)',
            '& .MuiMenuItem-root': {
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85), transition: 'all 0.15s ease',
              '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
              '&.Mui-selected': { background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600, '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` } },
            },
          },
        },
      }}
      sx={{
        borderRadius: '10px', fontFamily: "'DM Sans', sans-serif",
        color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.45) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: NEON },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
        '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
        '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      }}
    >
      {children}
    </Select>
    {helperText && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: error ? '#F87171' : alpha(NEON, 0.5), mt: 0.4, mx: 1.75 }}>
        {helperText}
      </Typography>
    )}
  </FormControl>
);

// ─── StyledSwitch ─────────────────────────────────────────────────────────────
const StyledSwitch = ({ checked, onChange, label }) => (
  <FormControlLabel
    control={
      <Switch
        checked={checked}
        onChange={onChange}
        sx={{
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              color: NEON,
              '& + .MuiSwitch-track': {
                backgroundColor: alpha(NEON, 0.4),
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: alpha(TEXT_BODY, 0.15),
            opacity: 1,
            borderRadius: 6,
          },
          '& .MuiSwitch-thumb': {
            boxShadow: `0 0 6px ${alpha(NEON, 0.4)}`,
          },
        }}
      />
    }
    label={
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.875rem',
        color: alpha(TEXT_BODY, 0.75),
        userSelect: 'none',
      }}>
        {label}
      </Typography>
    }
  />
);

const collectSponsorTierValidationErrors = (src) => {
  const errors = {};
  if (!src.tier_name?.trim()) errors.tier_name = 'Tier name is required';
  if (src.sponsor_amount === '') errors.sponsor_amount = 'Sponsor amount is required';
  else {
    const amount = Number.parseFloat(src.sponsor_amount);
    if (Number.isNaN(amount) || amount < 0) errors.sponsor_amount = 'Please enter a valid amount';
  }
  return errors;
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function SponsorTierForm({ isEdit = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(isEdit);
  const [error,                   setError]                   = useState(null);
  const [success,                 setSuccess]                 = useState(null);
  const [validationErrors,        setValidationErrors]        = useState({});
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest,    setPendingUpdateRequest]    = useState(null);
  const [form, setForm] = useState({
    tier_name: '',
    sponsor_amount: '',
    benefit: '',
    logo_size: 'medium',
    is_show_description: true,
    is_show_contact: true,
  });

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'sponsor-tier',
    userId: currentUser?.id,
    isEditMode: isEdit,
    enabled: !dataLoading,
    formData: form,
    setFormData: setForm,
  });

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    if (isEdit && id) {
      (async () => {
        try {
          setDataLoading(true);
          const { data } = await api.get(`/sponsor-tiers/${id}`);
          setForm({
            tier_name:           data.tier_name || '',
            sponsor_amount:      data.sponsor_amount != null ? String(data.sponsor_amount) : '',
            benefit:             data.benefit || '',
            logo_size:           data.logo_size || 'medium',
            is_show_description: Boolean(data.is_show_description ?? true),
            is_show_contact:     Boolean(data.is_show_contact ?? true),
          });
        } catch {
          setError('Failed to load tier.');
          navigate('/sponsor-tiers');
        } finally { setDataLoading(false); }
      })();
    } else {
      setDataLoading(false);
    }
  }, [currentUser, hasPartnershipAccess, id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (validationErrors[name]) setValidationErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleToggle = (name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.checked }));
    if (error) setError(null);
  };

  const validateForm = (src = form) => {
    const errors = collectSponsorTierValidationErrors(src);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(getFirstFieldError(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const buildPayload = (src) => ({
    tier_name:           src.tier_name.trim(),
    sponsor_amount:      Number.parseFloat(src.sponsor_amount),
    benefit:             src.benefit?.trim() || null,
    logo_size:           src.logo_size,
    is_show_description: Boolean(src.is_show_description),
    is_show_contact:     Boolean(src.is_show_contact),
  });

  const confirmUpdate = async () => {
    const src = pendingUpdateRequest || form;
    const errors = collectSponsorTierValidationErrors(src);
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen: setConfirmUpdateDialogOpen,
        setPendingUpdateRequest,
      });
      return;
    }
    try {
      setLoading(true);
      setValidationErrors({});
      await api.put(`/sponsor-tiers/${id}`, buildPayload(pendingUpdateRequest));
      setSuccess('Tier updated successfully!');
      setTimeout(() => navigate('/sponsor-tiers', { state: { refreshList: Date.now() } }), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed.');
    } finally {
      setLoading(false);
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        setError(null);
        setValidationErrors({});
        setPendingUpdateRequest(form);
        setConfirmUpdateDialogOpen(true);
        return;
      }
      if (!validateForm()) return;
      setLoading(true);
      setValidationErrors({});
      await api.post('/sponsor-tiers', buildPayload(form));
      if (!isEdit) formDraft.clearDraft();
      setSuccess('Tier created successfully!');
      setTimeout(() => navigate('/sponsor-tiers', { state: { refreshList: Date.now() } }), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed.');
    } finally { setLoading(false); }
  };

  if (!currentUser || !hasPartnershipAccess) return null;
  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading sponsor tier form" size={56} variant="primary" />
    </Box>
  );

  const submitDisabled = loading || !form.tier_name.trim() || form.sponsor_amount === '';

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 6 }}>
      <style>{FORM_CSS}</style>

      <Header
        title={isEdit ? 'Edit Sponsor Tier' : 'Add Sponsor Tier'}
        subtitle="Amount, display name, and benefits for this tier"
        icon={MoneyIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        backButtonText="Back to Tiers"
      />

      <FeedbackMessage
        message={success} severity="success" onClose={() => setSuccess(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(NEON3, 0.12), border: `1px solid ${alpha(NEON3, 0.25)}`, color: alpha(NEON3, 0.95) }}
      />
      <FeedbackMessage
        message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <SectionCard title="Tier Information" icon={<MoneyIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5 }}>
                  <StyledField
                    fullWidth required
                    label="Tier Name"
                    name="tier_name"
                    value={form.tier_name}
                    onChange={handleChange}
                    error={!!validationErrors.tier_name}
                    helperText={validationErrors.tier_name}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TitleIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledField
                    fullWidth required
                    label="Sponsor Amount (MYR)"
                    name="sponsor_amount"
                    type="number"
                    value={form.sponsor_amount}
                    onChange={handleChange}
                    error={!!validationErrors.sponsor_amount}
                    helperText={validationErrors.sponsor_amount}
                    inputProps={{ min: 0, step: '0.01' }}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <StyledField
                  fullWidth
                  label="Benefit Description"
                  name="benefit"
                  multiline
                  rows={4}
                  value={form.benefit}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <NotesIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledSelect
                  label="Logo Size (public card)"
                  name="logo_size"
                  value={form.logo_size}
                  onChange={handleChange}
                  startIcon={<SizeIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                >
                  {LOGO_SIZE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </StyledSelect>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: '10px',
                    border: `1px solid ${alpha(NEON, 0.45)}`,
                    background: alpha(SURFACE_UP, 0.7),
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.75rem',
                      color: alpha(NEON, 0.55),
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15, color: alpha(NEON, 0.45) }} />
                    Public card visibility
                  </Typography>
                  <StyledSwitch
                    checked={form.is_show_description}
                    onChange={handleToggle('is_show_description')}
                    label="Show Description"
                  />
                  <StyledSwitch
                    checked={form.is_show_contact}
                    onChange={handleToggle('is_show_contact')}
                    label="Show Contact"
                  />
                </Box>
              </Box>
            </SectionCard>

            <Box
              sx={{
                borderRadius: '16px',
                border: `1px solid ${alpha(NEON, 0.14)}`,
                background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.5)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
                p: 2.5,
              }}
            >
              <PartnerPublicPreview tierDraft={form} />
            </Box>
          </Box>

          {/* Submit */}
          <SubmitActionsCard
            statusText={isEdit ? 'Save changes to update this tier.' : 'Complete the form to add a new sponsor tier.'}
            missingRequiredFields={isEdit ? null : [!form.tier_name.trim() && 'Tier Name', form.sponsor_amount === '' && 'Sponsor Amount'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create sponsor tier."
            cancelLabel="Cancel"
            onCancel={() => navigate(-1)}
            cancelDisabled={loading}
            submitLabel={isEdit ? 'Update Tier' : 'Create Tier'}
            submitDisabled={submitDisabled}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />

        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        onConfirm={confirmUpdate}
        title="Confirm Update"
        variant="edit"
        itemType="tier"
        itemName={form.tier_name}
        description="This action will update the sponsor tier details and cannot be undone."
        confirmLabel={isEdit ? 'Update Tier' : 'Create Tier'}
        loading={loading}
      />
    </Container>
  );
}
